import React, { Ref } from 'react';
import styled from 'styled-components';
import { ProductImageDetails } from '@inter-ikea-storageone/range';
import {
  extractImageSource,
  imageHoverTransition,
  imageLoadingPlaceholder,
  ratioToPercentString
} from 'utils/imageUtils';
import { isMobile } from 'utils/platform';

type ProductImageProps = {
  image: ProductImageDetails;
  sizes?: string;
  alt: string;
  hoverImage?: ProductImageDetails;
  hoverContainerClass?: string;
};

const Image = styled.img`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  object-fit: contain;
  ${imageLoadingPlaceholder}
`;

const AspectRatio = styled.div<{ ratio: number }>`
  position: relative;
  height: 0;
  width: 100%;
  padding-bottom: ${props => ratioToPercentString(props.ratio)};
`;

// using a wrapping div instead of clip-path since Safari does not support animating clip-path
const HoverImageClipRect = styled.div<{ hoverContainerClass: string }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  transition: ${imageHoverTransition()};

  @media (hover: hover) {
    ${props => props.hoverContainerClass}:hover & {
      opacity: 1;
    }
  }
`;

const HoverImage = styled.img<{ hoverContainerClass: string }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  transition: ${imageHoverTransition()};

  @media (hover: hover) {
    ${props => props.hoverContainerClass}:hover & {
      transform: scale(1.1389);
    }
  }
`;

const ProductImage = React.forwardRef(
  (
    {
      image,
      sizes,
      alt,
      hoverImage,
      hoverContainerClass = AspectRatio,
      ...props
    }: ProductImageProps & React.CSSProperties & React.HTMLAttributes<HTMLImageElement>,
    ref: Ref<any>
  ) => {
    const { srcset, fallbackImage } = extractImageSource(image.sources);

    let hoverImageWrapper = null;
    if (hoverImage) {
      const { fallbackImage: hoverFallbackImage, srcset: hoverSrcset } = extractImageSource(
        hoverImage.sources
      );
      hoverImageWrapper = isMobile() ? null : (
        <HoverImageClipRect hoverContainerClass={hoverContainerClass}>
          <HoverImage
            src={hoverFallbackImage.src}
            sizes={sizes}
            srcSet={hoverSrcset}
            hoverContainerClass={hoverContainerClass}
            alt=""
          />
        </HoverImageClipRect>
      );
    }

    return (
      <AspectRatio ratio={fallbackImage.width / fallbackImage.height} {...props}>
        <Image
          src={fallbackImage.src}
          srcSet={srcset}
          sizes={sizes}
          width={fallbackImage.width}
          height={fallbackImage.height}
          draggable={false}
          loading="eager"
          alt={alt}
          ref={ref}
        />
        {hoverImageWrapper}
      </AspectRatio>
    );
  }
);

export default ProductImage;
